/**
 * Usage analytics — record a session on app load, and read the analytics for the
 * Insights page.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SessionPingInput, UsageResponse } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

export function recordSession(body: SessionPingInput): Promise<{ ok: boolean }> {
  return apiCall<{ ok: boolean }>('/me/sessions', { method: 'POST', body });
}

/** Fire one session ping per app load, once the user is authenticated. Reports
 *  whether we're running as an installed PWA (display-mode) — the server reads
 *  the rest (device type / OS / browser) from the User-Agent. Best-effort. */
export function useRecordSession(enabled: boolean): void {
  const fired = useRef(false);
  useEffect(() => {
    if (!enabled || fired.current) return;
    fired.current = true;

    let standalone = false;
    try {
      standalone =
        window.matchMedia?.('(display-mode: standalone)').matches === true ||
        (navigator as unknown as { standalone?: boolean }).standalone === true;
    } catch {
      /* matchMedia unavailable */
    }
    let timezone: string | null = null;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
      /* Intl unavailable */
    }

    void recordSession({ standalone, timezone }).catch(() => {
      /* analytics is non-critical — never surface a failure to the user */
    });
  }, [enabled]);
}

/** Usage analytics for the Insights "App usage" section. */
export function useUsage(from: string, to: string, enabled = true) {
  return useQuery<UsageResponse>({
    queryKey: ['usage', from, to],
    enabled,
    queryFn: () => apiCall<UsageResponse>(`/insights/usage?from=${from}&to=${to}`),
    staleTime: 60_000,
  });
}
